import { IAutoplayInfo } from 'library/ts/interfaces/IAutoplayInfo';
import { IPromiseResolveGeneric } from 'library/ts/interfaces/IPromiseResolveGeneric';
import { ContentType } from 'library/ts/model/enums/content/ContentType';
import { IResourceConfig } from 'library/ts/player/adapter/interfaces/resource/config/IResourceConfig';
import { IPlayerAdapter } from 'library/ts/player/adapter/IPlayerAdapter';
import { IPlayerAdapterAPI } from 'library/ts/player/adapter/IPlayerAdapterAPI';
import { IMobileWebUIManager } from 'plugins/tag/plugin_mobile_web_ui/ts/Application/interfaces/IMobileWebUIManager';
import { AbstractTagClass } from 'tag/ts/tag/abstract/AbstractTagClass';
import { IVideoApGetCurrentTime } from 'tag/ts/tag/interfaces/api/IVideoApGetCurrentTime';
import { ITagAbstractClassArgument } from 'tag/ts/tag/interfaces/arguments/ITagAbstractClassArgument';
import { ITagEventVO } from 'tag/ts/tag/interfaces/events/ITagEventVO';
import { INerdModeManager } from 'tag/ts/tag/interfaces/nerdmode/INerdModeManager';
import { ITagPlayerManager } from 'tag/ts/tag/interfaces/player/ITagPlayerManager';
import { IPlayerEventOrchestrator } from 'tag/ts/tag/player/managers/player/interfaces/IPlayerEventOrchestrator';
import { IPlayerLifecycleManager } from 'tag/ts/tag/player/managers/player/interfaces/IPlayerLifecycleManager';
import { IPlayerStateManager } from 'tag/ts/tag/player/managers/player/interfaces/IPlayerStateManager';
import { IResourceManager } from 'tag/ts/tag/player/managers/player/interfaces/IResourceManager';
import { PlayerEventOrchestrator } from 'tag/ts/tag/player/managers/player/PlayerEventOrchestrator';
import { PlayerLifecycleManager } from 'tag/ts/tag/player/managers/player/PlayerLifecycleManager';
import { PlayerStateManager } from 'tag/ts/tag/player/managers/player/PlayerStateManager';
import { ResourceManager } from 'tag/ts/tag/player/managers/player/ResourceManager';

/**
 * @copyright © Paramount Streaming
 * @author  Stan Gletski
 * @description **REFACTORED** Smart Tag Player Manager - Coordination Layer
 *
 * **Architecture Evolution:**
 * Refactored from monolithic 924-line class to focused coordination layer (~200 lines).
 * Core functionality extracted into specialized managers for better maintainability,
 * testability, and separation of concerns.
 *
 * **Manager Coordination:**
 * - **PlayerStateManager**: Time tracking, autoplay, content type management
 * - **PlayerLifecycleManager**: Window cleanup, player destruction, tag reset operations
 * - **ResourceManager**: Resource loading, configuration, owner ID management
 * - **PlayerEventOrchestrator**: Event routing, player adapter integration, initialization
 *
 * **Coordination Responsibilities:**
 * - Manager instantiation and initialization
 * - Cross-manager communication facilitation
 * - Interface compliance through delegation
 * - Unified error handling and logging
 * - Manager lifecycle coordination
 *
 * **Memory Leak Prevention:**
 * Enhanced through specialized manager cleanup:
 * - PlayerLifecycleManager: Window event cleanup
 * - PlayerEventOrchestrator: Player adapter cleanup
 * - ResourceManager: Resource cleanup
 * - PlayerStateManager: State reset
 *
 * **Benefits of Refactoring:**
 * - **78% code reduction**: 924 → ~200 lines
 * - **Focused testing**: Individual manager unit tests
 * - **Clear separation**: Single responsibility per manager
 * - **Enhanced maintainability**: Changes isolated to specific concerns
 * - **Improved reliability**: Dedicated cleanup logic per manager
 */
export class PlayerManager extends AbstractTagClass implements ITagPlayerManager {

	// Specialized managers for focused responsibilities
	#stateManager: IPlayerStateManager;
	#lifecycleManager: IPlayerLifecycleManager;
	#resourceManager: IResourceManager;
	#eventOrchestrator: IPlayerEventOrchestrator;

	constructor( argument: ITagAbstractClassArgument ) {
		super(
			{
				...argument,
				name: 'PlayerManager',
				onPluginsPhaseDone: () => {
					this?.logger?.log( 'onPluginsPhaseDone' );

					// Nerd Mode and Mobile Web UI both self-register as standard Tag-level
					// plugins (`owner: 'PlayerManager'`, see `TagConfigNerdMode.ts` /
					// `TagConfigMobileWebUI.ts`) and are constructed here by
					// `PluginUtilities.setupPluginsForOwnerClass()` — but that mechanism only
					// ever calls `new pluginConfig.object(...)`, nothing downstream calls
					// `initialize()` for them the way `AviaLifecycleManager` used to before
					// Nerd Mode became a plugin. This is the first point after construction
					// where the owner is notified the plugin phase is done, so it's the
					// natural place to run both — no-op if a plugin was never triggered/loaded.
					( this.plugins?.NerdMode?.instance as INerdModeManager | undefined )?.initialize();
					( this.plugins?.MobileWebUI?.instance as IMobileWebUIManager | undefined )?.initialize();
				}
			}
		);

		// Initialize specialized managers
		this.#stateManager = new PlayerStateManager( this.tag?.name );
		this.#lifecycleManager = new PlayerLifecycleManager( this.tag, this.utils );
		this.#resourceManager = new ResourceManager( this.tag, this.utils );
		this.#eventOrchestrator = new PlayerEventOrchestrator( this.tag, this.utils );

		this?.logger?.log( 'PlayerManager created with specialized managers' );
	}

	/**
	 * @description Returns instance of player adapter class
	 * @delegate PlayerEventOrchestrator
	 */
	get playerAdapter(): IPlayerAdapter {
		return this.#eventOrchestrator.playerAdapter;
	}

	/**
	 * @description Returns VTG player instance
	 * @delegate PlayerEventOrchestrator
	 */
	get player(): IPlayerAdapterAPI {
		return this.#eventOrchestrator.player;
	}

	/**
	 * @description Returns current content type
	 * @delegate PlayerStateManager
	 */
	get contentType(): ContentType | undefined {
		return this.#stateManager.contentType;
	}

	/**
	 * @description Returns autoplay detection results
	 * @delegate PlayerStateManager
	 */
	get autoplay(): IAutoplayInfo {
		return this.#stateManager.autoplay;
	}

	/**
	 * @description Returns current resource config object
	 * @delegate ResourceManager
	 */
	get currentResource(): IResourceConfig {
		return this.#resourceManager.currentResource;
	}

	/**
	 * @description Returns promise that resolves with resource config object
	 * @delegate ResourceManager
	 */
	get resourceConfig(): Promise<IResourceConfig> {
		return this.#resourceManager.resourceConfig;
	}

	/**
	 * @description Returns current playback time for Chromecast sender
	 * @delegate PlayerStateManager
	 */
	get currentTime(): IVideoApGetCurrentTime {
		return this.#stateManager.currentTime;
	}

	/**
	 * @description State manager for player time tracking and autoplay
	 */
	get stateManager(): IPlayerStateManager {
		return this.#stateManager;
	}

	/**
	 * @description Lifecycle manager for player cleanup and reset operations
	 */
	get lifecycleManager(): IPlayerLifecycleManager {
		return this.#lifecycleManager;
	}

	/**
	 * @description Resource manager for resource loading and configuration
	 */
	get resourceManager(): IResourceManager {
		return this.#resourceManager;
	}

	/**
	 * @description Event orchestrator for player adapter coordination
	 */
	get eventOrchestrator(): IPlayerEventOrchestrator {
		return this.#eventOrchestrator;
	}

	/**
	 * @description Initializes PlayerManager and all specialized managers
	 * @delegate PlayerEventOrchestrator (primary initialization)
	 */
	initialize = (): Promise<IPromiseResolveGeneric> => {
		return new Promise(
			( resolve, reject ) => {
				this?.logger?.log( 'initialize: Starting PlayerManager coordination layer' );

				// Initialize event orchestrator (handles player adapter + events)
				this.#eventOrchestrator.initialize().then(
					( data ) => {
						this?.logger?.log( 'initialize: PlayerManager coordination complete', data );
						resolve( { msg: 'PlayerManager initialized', success: true, result: this } );
					}
				).catch(
					( error ) => {
						this?.logger?.error( 'initialize: PlayerManager coordination failed:', error );
						reject( {
							msg: 'Failed to initialize PlayerManager',
							success: false,
							error: error,
							context: 'PlayerManager.initialize'
						} );
					}
				);
			}
		);
	};

	/**
	 * @description Loads Tag's default resource configuration into the player
	 * @delegate ResourceManager
	 * @returns {Promise<IPromiseResolveGeneric>} Promise resolving when resource loading completes
	 */
	loadDefaultResource(): Promise<IPromiseResolveGeneric> {
		return this.#resourceManager.loadDefaultResource();
	}

	/**
	 * @description Loads custom resource configuration into the player
	 * @delegate ResourceManager
	 * @param {IResourceConfig} customConfig - Custom resource configuration to load
	 * @returns {Promise<IPromiseResolveGeneric>} Promise resolving when resource loading completes
	 */
	loadCustomResource( customConfig: IResourceConfig ): Promise<IPromiseResolveGeneric> {
		return this.#resourceManager.loadCustomResource( customConfig );
	}

	/**
	 * @description Player Adapter Event handler
	 * @delegate PlayerEventOrchestrator
	 */
	onPlayerAdapterEvent = ( tagEvent: ITagEventVO ): void => {
		this.#eventOrchestrator.onPlayerAdapterEvent( tagEvent );
	};

}